import { FaCalendarAlt, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaQrcode } from "react-icons/fa";
import "./TicketCard.css";

/**
 * Displays a single ticket card in the wallet.
 *
 * Props:
 *  - ticket: ticket object from API
 *  - onClick: () => void (navigate to ticket detail)
 *  - expanded: boolean — show QR code inline
 */
export default function TicketCard({ ticket, onClick, expanded = false }) {
  const isInactive = ticket.status === "cancelled" || ticket.reservation_payment_status === "refunded";

  const statusConfig = {
    valid: { label: "Valid", className: "ticket-status--valid", Icon: FaCheckCircle },
    used: { label: "Used", className: "ticket-status--used", Icon: FaCheckCircle },
    cancelled: {
      label: ticket.reservation_payment_status === "refunded" ? "Refunded" : "Cancelled",
      className: "ticket-status--cancelled",
      Icon: FaTimesCircle,
    },
    transferred: { label: "Transferred", className: "ticket-status--transferred", Icon: null },
  };

  const config = statusConfig[ticket.status] || statusConfig.valid;
  const StatusIcon = config.Icon;

  const TZ = "America/Chicago";

  const eventDate = ticket.start_at
    ? new Date(ticket.start_at).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: TZ,
      })
    : "TBD";

  const fmtTime = (dt) => dt
    ? new Date(dt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: TZ })
    : null;

  // Show valid_from–valid_until from the tier; fall back to event start_at/end_at for missing bound
  const eventTime = (() => {
    const vf = fmtTime(ticket.valid_from) || fmtTime(ticket.start_at);
    const vu = fmtTime(ticket.valid_until) || fmtTime(ticket.end_at);
    if (vf && vu && vf !== vu) return `${vf} – ${vu}`;
    return vf || vu || "";
  })();

  const isPast = ticket.start_at && new Date(ticket.start_at) < new Date();

  return (
    <div
      className={`ticket-card ${isPast ? "ticket-card--past" : ""} ${isInactive ? "ticket-card--inactive" : ""}`}
      onClick={isInactive ? undefined : onClick}
      role={isInactive ? undefined : "button"}
      tabIndex={isInactive ? -1 : 0}
      onKeyDown={isInactive ? undefined : (e) => (e.key === "Enter" || e.key === " ") && onClick?.()}
    >
      {/* Left accent strip */}
      <div className={`ticket-card-strip ticket-card-strip--${ticket.status}`} />

      <div className="ticket-card-content">
        <div className="ticket-card-header">
          <div className="ticket-card-event-name">{ticket.event_title}</div>
          <div className={`ticket-status ${config.className}`}>
            {StatusIcon && <StatusIcon />}
            <span>{config.label}</span>
          </div>
        </div>

        <div className="ticket-card-tier">{ticket.tier_name}</div>

        <div className="ticket-card-meta">
          <div className="ticket-meta-item">
            <FaCalendarAlt />
            <span>{eventDate}{eventTime && ` · ${eventTime}`}</span>
          </div>
          {ticket.venue_name && (
            <div className="ticket-meta-item">
              <FaMapMarkerAlt />
              <span>{ticket.venue_name}</span>
            </div>
          )}
        </div>

        <div className="ticket-card-footer">
          {ticket.attendee_name && (
            <span className="ticket-attendee">{ticket.attendee_name}</span>
          )}
          {!expanded && !isInactive && (
            <div className="ticket-qr-hint">
              <FaQrcode />
              <span>Tap to view QR</span>
            </div>
          )}
        </div>

        {expanded && !isInactive && (
          <div className="ticket-card-qr-inline">
            {ticket.qr_image_url ? (
              <img
                src={ticket.qr_image_url}
                alt="Ticket QR Code"
                className="ticket-card-qr-image"
              />
            ) : (
              <div className="ticket-card-qr-placeholder">
                <FaQrcode />
                <span>QR generating…</span>
              </div>
            )}
            <p className="ticket-card-qr-code">
              <code>{String(ticket.ticket_code).slice(0, 8).toUpperCase()}…</code>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
